import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import { Navbar, Footer, Loading, Breadcrumbs } from "../../components/shared";
import { getFamilyDonations, updateDonation } from "../../service/DonationApi";
import { useParams, useNavigate } from "react-router-dom";
import { useCheckAccess, firestoreToDateInput } from "../../utils/Utilities";
import '../../pages/general/General.css';
import { useSelector } from "react-redux";
const DonationView = () => {
  useCheckAccess('DonationRW');
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const queryParams = new URLSearchParams(window.location.search);
  const guestId = queryParams.get("guestId");
  const user = useSelector((state) => state.user);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [originalDonation, setOriginalDonation] = useState(null);
  useEffect(() => {
    const fetchFamilyDonations = async () => {
      setLoading(true);
      try {
        const response = await getFamilyDonations({
          familyId: id === 'guest' ? '' : id,
          guestId: guestId || '',
        });
        setDonations(response || []);
      } catch (error) {
        console.error('Error fetching donations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFamilyDonations();
  }, [id, guestId]);
  // Check if the form has changes compared to original
  const hasChanges = (form, original) => {
    if (!form || !original) return false;
    return (
      (form.externalTransactionReference || "") !== (original.externalTransactionReference || "") ||
      (form.receiptNo || "") !== (original.receiptNo || "") ||
      (firestoreToDateInput(form.transactionDate) || "") !== (firestoreToDateInput(original.transactionDate) || "") ||
      (form.amount || 0) !== (original.amount || 0)
    );
  };

  // Save donation changes
  const handleSave = async (donationId) => {
    try {
      // Build payload with only changed fields
      const payload = { donationId };

      if ((editForm.externalTransactionReference || "") !== (originalDonation.externalTransactionReference || "")) {
        payload.externalTransactionReference = editForm.externalTransactionReference || null;
      }
      if ((editForm.receiptNo || "") !== (originalDonation.receiptNo || "")) {
        payload.receiptNo = editForm.receiptNo || null;
      }
      if (user?.roles?.includes('SuperUser') && (editForm.amount || 0) !== (originalDonation.amount || 0)) {
        payload.amount = parseFloat(editForm.amount);
      }

      // If nothing changed, do not send request
      if (Object.keys(payload).length === 0) return;

      await updateDonation(payload);

      // Refresh full donations list
      setLoading(true);
      const response = await getFamilyDonations({
        familyId: id === 'guest' ? '' : id,
        guestId: guestId || '',
      });
      setDonations(response || []);
      setEditingId(null);
    } catch (err) {
      console.error("Error updating donation:", err);
    } finally {
      setLoading(false);
    }
  };




  if (loading) {
    return (
      <>
        <Navbar />
        <div className="general_parent">
          <div className="general_container">
            <div className="general_body">
              <Breadcrumbs /><br />
              <Loading />
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <div>
      <Navbar />
      <Helmet>
        <title>View Family Donations</title>
      </Helmet>

      <div className="general_parent">
        <div className="general_container">
          <div className='general_body'>
            <Breadcrumbs /><br />
            <h2>நன்கொடை விவரங்கள்</h2>
            <br />
            <h3>{id === 'guest' ? ('விருந்தினர் எண்: ' + guestId) : ('குடும்ப எண்: ' + id)}</h3>
            <br />
            {donations.length === 0 ? (
              <p>நன்கொடைகள் எதுவும் கிடைக்கவில்லை..</p>
            ) : (
              <div className="table-container">
                <table className="table" aria-label="நன்கொடை விவரங்கள்">

                  <tbody>
                    {Object.entries(
                      donations.reduce((acc, donation) => {
                        const cat = donation.category || "மற்றவை";
                        if (!acc[cat]) acc[cat] = [];
                        acc[cat].push(donation);
                        return acc;
                      }, {})
                    )
                      // sort categories by latest transaction date
                      .sort(([, a], [, b]) => {
                        const latestA = Math.max(...a.map(d => d.transactionDate?._seconds || 0));
                        const latestB = Math.max(...b.map(d => d.transactionDate?._seconds || 0));
                        return latestB - latestA; // descending order
                      })
                      .map(([category, catDonations]) => {
                        const total = catDonations.reduce((sum, d) => sum + (d.amount || 0), 0);

                        return (
                          <React.Fragment key={category}>
                            <div className="table-container">
                              <table className="table" aria-label={`${category} நன்கொடைகள்`}>
                                <thead>
                                  <tr>
                                    <th>{category}</th>
                                    <th>விவரங்கள்</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {catDonations.map((donation) => (
                                    <tr key={donation.id}>
                                      <td>
                                        {donation.category} - {donation.subcategory}
                                      </td>
                                      <td>
                                        {editingId === donation.id ? (
                                          <>
                                            {/* Editable fields */}
                                            <div className="detail-item">
                                              <label className="detail-label">பரிவர்த்தனை குறிப்பு:</label>
                                              <input
                                                type="text"
                                                value={editForm.externalTransactionReference || ""}
                                                placeholder="உதாரணம் GPay எண்"
                                                onChange={(e) =>
                                                  setEditForm({ ...editForm, externalTransactionReference: e.target.value })
                                                }
                                              />
                                            </div>
                                            <div className="detail-item">
                                              <label className="detail-label">ரசீது எண்:</label>
                                              <input
                                                type="text"
                                                pattern="[0-9]*"
                                                placeholder="1234"
                                                inputMode="numeric"
                                                value={editForm.receiptNo || ""}
                                                onChange={(e) =>
                                                  setEditForm({ ...editForm, receiptNo: e.target.value })
                                                }
                                              />
                                            </div>
                                            {/* Amount only if super user */}
                                            {user?.roles?.includes('SuperUser') && (
                                              <div className="detail-item">
                                                <label className="detail-label">தொகை: </label>
                                                <input
                                                  type="text"
                                                  pattern="^(0|[1-9]\d*)(\.\d{1,2})?$"
                                                  inputMode="numeric"
                                                  value={editForm.amount || ""}
                                                  onChange={(e) =>
                                                    setEditForm({ ...editForm, amount: e.target.value })
                                                  }
                                                />
                                              </div>
                                            )}

                                            {/* Action buttons */}
                                            <button
                                              onClick={() => handleSave(donation.id)}
                                              disabled={!hasChanges(editForm, originalDonation)}
                                            >
                                              💾 Save
                                            </button>

                                            <button onClick={() => setEditingId(null)}>❌ Cancel</button>
                                          </>
                                        ) : (
                                          <>
                                            {donation.familyMemberFirstName ? `${donation.familyMemberFirstName} ` : ""} {donation.familyMemberLastName ? `${donation.familyMemberLastName} ` : ""}
                                            {donation.transactionDate
                                              ? new Date(donation.transactionDate._seconds * 1000).toLocaleDateString()
                                              : ""}{" "}
                                            அன்று ₹{donation.amount} செலுத்தினார்{" "}
                                            {donation.externalTransactionReference && (
                                              <> குறிப்பு:{donation.externalTransactionReference}</>
                                            )}
                                            {(donation.receiptNo || donation.onlineReceiptNo) && (
                                              <>
                                                {donation.receiptNo && <> ரசீது எண்: {donation.receiptNo}</>}
                                                {donation.receiptNo && donation.onlineReceiptNo && " | "}
                                                {donation.onlineReceiptNo && <> இ.ரசீது எண்: {donation.onlineReceiptNo}</>}
                                              </>
                                            )}
                                            {!guestId && (
                                              <button
                                                onClick={() => {
                                                  setEditingId(donation.id);
                                                  setEditForm({ ...donation });
                                                  setOriginalDonation({ ...donation }); // keep original for comparison
                                                }}
                                              >
                                                ✏️ Edit
                                              </button>
                                            )}
                                          </>
                                        )}
                                      </td>
                                    </tr>
                                  ))}

                                  {/* total row per category */}
                                  <tr>
                                    <td style={{ textAlign: "right", fontWeight: "bold" }}>
                                      {category} மொத்தம்:
                                    </td>
                                    <td style={{ fontWeight: "bold" }}>₹{total}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </React.Fragment>
                        );
                      })}

                  </tbody>

                </table>

              </div>
            )}

            <br />
            <button type="button" onClick={() => navigate(`/donation/create`)}>➕ புதிய நன்கொடையைச் சேர்க்கவும்</button>
            <button type="button" onClick={() => navigate(`/donation`)}>🏠 நன்கொடை நிர்வாகம்</button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DonationView;
